/**
 * Short, player-facing Session 0 pages for CHEATSHEET.app. This is deliberately a primer,
 * not a setting encyclopedia or a rules substitute: it gives a new player enough shared
 * vocabulary to make a character and join the first scene with confidence.
 *
 * Sources: Lancer Core Rulebook pp.10–14 and 258–259. Brief quotations are callouts where
 * the book's own framing does more work than a paraphrase; the rest is original guidance.
 */

export function SettingPage() {
  return (
    <div className="gms-cheatsheet-page">
      <h2 className="gms-cheatsheet-page-title">THE SETTING</h2>

      <section className="gms-cheatsheet-block">
        <h3>THE SHORT VERSION</h3>
        <p>
          Lancer is hopeful, messy far-future science fiction. It is the year 5016u: humanity has spread through the stars, and
          its central government, Union, is building a post-scarcity future. That future is real in the galactic Core—and much
          less settled, safe, or evenly shared at its edges.
        </p>
        <blockquote className="gms-cheatsheet-quote">“At the core of humanity’s territory there is a golden age.”</blockquote>
        <p className="gms-cheatsheet-cite">Core Rulebook p.10</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>THREE WORDS TO KNOW</h3>
        <div className="gms-cheatsheet-glossary">
          <div><strong>Union</strong><span>The vast human polity trying to extend a better future across the galaxy.</span></div>
          <div><strong>Blinkspace</strong><span>The strange parallel medium crossed through blink gates; it makes interstellar travel possible.</span></div>
          <div><strong>Omninet</strong><span>The galaxy-spanning information network: communication, culture, records, and more.</span></div>
        </div>
        <p>Most people still live ordinary lives on a homeworld, moon, or station. Distance and local history matter: a place can feel close to Union, far from it, or caught in the consequences of both.</p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.10–11</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>WHO YOU ARE</h3>
        <p>
          You play a <strong>lancer</strong>: an exceptional, human mech pilot. Your pilot can come from any culture, station, class,
          or history in the diaspora. They are remarkable, but not invulnerable—and the people, places, and ideals they care about
          are the point.
        </p>
        <blockquote className="gms-cheatsheet-quote">“Your character is a mechanized cavalry pilot of particular note.”</blockquote>
        <p><strong>Useful character questions:</strong> What place made you? Who taught you to pilot? What future are you willing to fight for? What do you refuse to become?</p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.11</p>
      </section>
    </div>
  );
}

export function AtTheTablePage() {
  return (
    <div className="gms-cheatsheet-page">
      <h2 className="gms-cheatsheet-page-title">AT THE TABLE</h2>

      <section className="gms-cheatsheet-block">
        <h3>TWO MODES, ONE STORY</h3>
        <div className="gms-cheatsheet-rules-grid">
          <div className="gms-cheatsheet-rule-card"><span className="gms-cheatsheet-rule-kicker">Narrative play</span><p>Describe what your pilot does off the battlefield: investigate, negotiate, travel, improvise, and make trouble. The fiction leads.</p></div>
          <div className="gms-cheatsheet-rule-card"><span className="gms-cheatsheet-rule-kicker">Mech combat</span><p>When the fight matters, play turns on a tactical map. Position, actions, ranges, and teamwork become precise.</p></div>
        </div>
        <p>Neither mode is the “real” game. A strong Lancer campaign moves between character-driven scenes and sharp tactical battles.</p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.12</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>HOW TO PLAY A SCENE</h3>
        <ol className="gms-cheatsheet-steps">
          <li>Say what your pilot is trying to do, in the fiction.</li>
          <li>Answer questions about how they approach it and what is at stake.</li>
          <li>Roll only when the result is uncertain and meaningful; the app can help when a roll is called for.</li>
          <li>Let the result change the situation, then decide what your character does next.</li>
        </ol>
        <blockquote className="gms-cheatsheet-quote">“Your character is, first and foremost, a pilot.”</blockquote>
        <p className="gms-cheatsheet-cite">Core Rulebook p.12–13</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>DON’T MEMORIZE THE BOOK</h3>
        <p>Bring your ideas, ask questions, and name what you want to try. Your GM frames the world and rulings; everyone helps make the story. You will learn the tactical layer by playing it together.</p>
        <p className="gms-cheatsheet-callout"><strong>Table habit:</strong> describe intent before reaching for a button or a rule. “I want to get them out alive” gives the table something much better to build on than “what can I roll?”</p>
      </section>
    </div>
  );
}

export function SessionZeroPage() {
  return (
    <div className="gms-cheatsheet-page">
      <h2 className="gms-cheatsheet-page-title">SESSION ZERO</h2>

      <section className="gms-cheatsheet-block">
        <h3>MAKE THE CAMPAIGN TOGETHER</h3>
        <p>Session 0 is for aligning on the game you want: combat-forward, political, exploration-heavy, strange, funny, serious, or some mix. Share content boundaries, house rules, scheduling expectations, and the kind of spotlight everyone wants.</p>
        <blockquote className="gms-cheatsheet-quote">“Clearly setting expectations helps players figure out if they’re interested in the kind of game you’re going to run.”</blockquote>
        <p className="gms-cheatsheet-cite">Core Rulebook p.258</p>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>BRING THESE ANSWERS</h3>
        <ul className="gms-cheatsheet-list">
          <li>A callsign, a rough background, and one thing your pilot wants.</li>
          <li>A reason your pilot joined this lance—and one thing they need from its other members.</li>
          <li>One connection to the campaign’s starting place: a friend, debt, memory, job, rival, or promise.</li>
          <li>Any subject you would prefer the table fade out, skip, or check in about.</li>
        </ul>
      </section>

      <section className="gms-cheatsheet-block">
        <h3>YOUR FIRST MECH</h3>
        <p>Starting at License Level 0 keeps the first build approachable. Pick what sounds fun; you are not locking yourself into a permanent “class.” Pilots grow, earn licenses, and can refit between missions.</p>
        <p className="gms-cheatsheet-callout"><strong>Good enough is ready enough.</strong> A clear concept and a reason to work with the team matter more than an optimized first build.</p>
        <p className="gms-cheatsheet-cite">Core Rulebook p.258; pilot/mech advancement pp.18–19</p>
      </section>
    </div>
  );
}
